import { useState, useEffect, useCallback, useRef } from 'react'
import { openDB, IDBPDatabase } from 'idb'

// ─── Types ────────────────────────────────────────────────────
interface QueuedWrite {
  id: string
  table: 'attendance' | 'test_scores'
  payload: any
  timestamp: number
  retries: number
}

interface OfflineSyncState {
  isOnline: boolean
  pendingCount: number
  lastSyncAt: Date | null
  isSyncing: boolean
}

const DB_NAME = 'edtrack-offline'
const DB_VERSION = 1
const STORE_NAME = 'pending-writes'

// ─── Open IndexedDB ───────────────────────────────────────────
async function getDB(): Promise<IDBPDatabase> {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('table', 'table')
        store.createIndex('timestamp', 'timestamp')
      }
    },
  })
}

// ─── Hook ─────────────────────────────────────────────────────
export function useOfflineSync(supabase: any) {
  const [state, setState] = useState<OfflineSyncState>({
    isOnline: navigator.onLine,
    pendingCount: 0,
    lastSyncAt: null,
    isSyncing: false,
  })
  const syncInProgress = useRef(false)

  // Count pending writes
  const refreshPendingCount = useCallback(async () => {
    try {
      const db = await getDB()
      const count = await db.count(STORE_NAME)
      setState(s => ({ ...s, pendingCount: count }))
    } catch {
      // IDB not available
    }
  }, [])

  // Queue a write for offline use
  const queueWrite = useCallback(async (table: 'attendance' | 'test_scores', payload: any) => {
    const item: QueuedWrite = {
      id: `${table}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      table,
      payload,
      timestamp: Date.now(),
      retries: 0,
    }
    try {
      const db = await getDB()
      await db.add(STORE_NAME, item)
      await refreshPendingCount()
    } catch (err) {
      console.error('Failed to queue write:', err)
      throw err
    }
  }, [refreshPendingCount])

  // Flush all pending writes to Supabase
  const flushQueue = useCallback(async () => {
    if (syncInProgress.current || !navigator.onLine) return
    syncInProgress.current = true
    setState(s => ({ ...s, isSyncing: true }))

    try {
      const db = await getDB()
      const pending: QueuedWrite[] = await db.getAll(STORE_NAME)

      if (pending.length === 0) {
        setState(s => ({ ...s, isSyncing: false, lastSyncAt: new Date() }))
        return
      }

      let successCount = 0
      for (const item of pending) {
        try {
          const { error } = await supabase.from(item.table).upsert(item.payload, {
            onConflict: item.table === 'attendance' ? 'student_id,date' : undefined,
          })
          if (!error) {
            await db.delete(STORE_NAME, item.id)
            successCount++
          } else {
            console.error(`Failed to sync ${item.table}:`, error.message)
            // Increment retries
            await db.put(STORE_NAME, { ...item, retries: item.retries + 1 })
          }
        } catch {
          await db.put(STORE_NAME, { ...item, retries: item.retries + 1 })
        }
      }

      const remaining = await db.count(STORE_NAME)
      setState(s => ({
        ...s,
        isSyncing: false,
        pendingCount: remaining,
        lastSyncAt: new Date(),
      }))

      if (successCount > 0) {
        console.log(`EdTrack: Synced ${successCount} offline writes`)
      }
    } catch (err) {
      console.error('Flush queue error:', err)
      setState(s => ({ ...s, isSyncing: false }))
    } finally {
      syncInProgress.current = false
    }
  }, [supabase])

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setState(s => ({ ...s, isOnline: true }))
      flushQueue()
    }
    const handleOffline = () => {
      setState(s => ({ ...s, isOnline: false }))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Listen for service worker sync complete
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === 'SYNC_COMPLETE') {
        refreshPendingCount()
        setState(s => ({ ...s, lastSyncAt: new Date() }))
      }
    }
    navigator.serviceWorker?.addEventListener('message', handleMessage)

    // Initial check
    refreshPendingCount()

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      navigator.serviceWorker?.removeEventListener('message', handleMessage)
    }
  }, [flushQueue, refreshPendingCount])

  // Flush on mount if online
  useEffect(() => {
    if (navigator.onLine) flushQueue()
  }, [flushQueue])

  return { ...state, queueWrite, flushQueue }
}

// ─── Offline-aware attendance save ───────────────────────────
export async function saveAttendanceOfflineAware(
  supabase: any,
  queueWrite: Function,
  isOnline: boolean,
  records: any[]
): Promise<{ success: boolean; queued: boolean }> {
  if (isOnline) {
    const { error } = await supabase
      .from('attendance')
      .upsert(records, { onConflict: 'student_id,date' })
    if (error) throw new Error(error.message)
    return { success: true, queued: false }
  } else {
    // Queue each record individually for IndexedDB
    for (const record of records) {
      await queueWrite('attendance', record)
    }
    return { success: true, queued: true }
  }
}

// ─── Offline-aware score save ─────────────────────────────────
export async function saveScoresOfflineAware(
  supabase: any,
  queueWrite: Function,
  isOnline: boolean,
  records: any[]
): Promise<{ success: boolean; queued: boolean }> {
  if (isOnline) {
    const { error } = await supabase.from('test_scores').insert(records)
    if (error) throw new Error(error.message)
    return { success: true, queued: false }
  } else {
    for (const record of records) {
      await queueWrite('test_scores', record)
    }
    return { success: true, queued: true }
  }
}
